"use client";

import type {
  LeadFlowIconItem as LeadFlowIconItemContent,
  LeadFlowRichTextContent,
} from "@homeinstead/schemas";

import Quote from "@homeinstead/icons/Quote";
import Quotes from "@homeinstead/icons/Quotes";
import Service from "@homeinstead/icons/Service";
import Image from "next/image";
import { LeadFlowPortableText } from "./LeadFlowPortableText";

const ICON_SIZE_PIXELS = 48;

/** These glyphs bake in a New Beginnings fill, so their paths are repainted. */
const SURFACE_ICON_CLASS_NAME =
  "size-12 shrink-0 [&_path]:fill-surface-foreground";

type IconItemProps = {
  item: {
    caption?: LeadFlowRichTextContent | null;
    customIconUrl?: string | null;
    icon?: LeadFlowIconItemContent["icon"] | null;
  };
};

function LeadFlowIcon({ customIconUrl, icon }: IconItemProps["item"]) {
  switch (icon) {
    case "default-quote-circle":
      return (
        <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white">
          <Quotes className="h-1/3 w-1/3 fill-new-beginnings-base" />
        </div>
      );
    case "quote":
      return <Quote className={SURFACE_ICON_CLASS_NAME} />;
    case "service":
      return <Service className={SURFACE_ICON_CLASS_NAME} />;
    case "custom":
      return customIconUrl ? (
        <Image
          alt=""
          className="size-12 shrink-0"
          height={ICON_SIZE_PIXELS}
          src={customIconUrl}
          width={ICON_SIZE_PIXELS}
        />
      ) : null;
    default:
      return null;
  }
}

function IconItem({ item }: IconItemProps) {
  return (
    <div
      className="flex flex-row items-start gap-4"
      data-testid="lead-flow-icon-item"
    >
      <LeadFlowIcon customIconUrl={item.customIconUrl} icon={item.icon} />
      <LeadFlowPortableText value={item.caption} />
    </div>
  );
}

export { IconItem };
